import moment from 'moment';
import type { ExpiryTimeGranularity } from '@x-guard/xgac-types/xgac';
import { useFormContext, useWatch } from 'react-hook-form';
import { useCallback, useEffect, useMemo } from 'react';
import { required, useTranslate, type Validator } from 'react-admin';
import {
  Box, Chip, Stack, Tooltip, Typography,
} from '@mui/material';
import { GrayDateInputWithUpperLabel } from './inputs/GrayDateInputWithUpperLabel';
import {
  isCertificateDateSource,
  useCertificateDateOriginsContext,
} from './CertificateDateOriginContext';
import {
  getExpiryTimeGranularity,
  getMinimumNextDate,
  TimeGranularityLabel,
  calculateDateAfterTimeGranularity,
} from '../../../utils/timeGranularityUtils';

export const TABLE_DATE_INPUT_WIDTH = 150;

export const TABLE_EXPIRY_GRID_TEMPLATE = `${TABLE_DATE_INPUT_WIDTH}px max-content ${TABLE_DATE_INPUT_WIDTH}px`;

export const useExpiryDate = (
  startDateSource: string,
  expiryDateSource: string,
  timeGranularity: ExpiryTimeGranularity | undefined,
) => {

  const { setValue } = useFormContext();
  const { setOrigin } = useCertificateDateOriginsContext();
  const startDate = useWatch({ name: startDateSource });
  const expiryDate = useWatch({ name: expiryDateSource });

  const calculatedExpiryDate = useMemo(() => {

    return calculateDateAfterTimeGranularity(startDate, timeGranularity);

  }, [startDate, timeGranularity]);

  // there are 3 cases when the expiry date is "applied" to the form:
  // 1. when the user clicks on the chip that shows the validity period value to reset the custom expiry date
  // 2. when the AI document analysis returns only the lastReceivedOn date, which is then used to calculate the expiry date
  // 3. when the start date is changed manually via date picker
  //
  // for the first case, the expiry date can be calculated from the observed start date value since the watched value is up-to-date at this moment.
  // for the second and third cases, this is not possible since the watched start date value is not yet updated by that time.
  // instead, the updated start date value must be passed explicitly.

  const applyExpiryDate = useCallback((updatedStartDate?: string) => {

    const updatedExpiryDate = updatedStartDate ? calculateDateAfterTimeGranularity(updatedStartDate, timeGranularity) : calculatedExpiryDate;

    if (!updatedExpiryDate) return;

    setValue(expiryDateSource, updatedExpiryDate, { shouldDirty: true, shouldValidate: true });

    if (isCertificateDateSource(expiryDateSource)) setOrigin(expiryDateSource, 'calculated');

  }, [calculatedExpiryDate, expiryDateSource, setOrigin, setValue, timeGranularity]);

  const isOverridden = useMemo(
    () => {

      return calculatedExpiryDate != null
      && expiryDate != null
      && moment(expiryDate).diff(moment(calculatedExpiryDate), 'days') !== 0;

    },
    [expiryDate, calculatedExpiryDate],
  );

  return {
    calculatedExpiryDate,
    applyExpiryDate,
    isOverridden,
    startDate,
    expiryDate,
  };

};

const ExpiryDateHelperText = ({ text, centerAlign = false }: { text: string; centerAlign?: boolean }) => {

  const translate = useTranslate();

  return (
    <Typography color="text.secondary"
      sx={{
        fontSize: 10,
        mt: 0.5,
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        ...(centerAlign
          ? { width: '100%', textAlign: 'center' }
          : { textAlign: 'left' }),
      }}>
      {translate(text)}
    </Typography>
  );

};

export const useRevalidateDatePair = (firstDateSource: string, secondDateSource?: string) => {

  const { trigger } = useFormContext();
  const firstValue = useWatch({ name: firstDateSource });
  const secondValue = useWatch({ name: secondDateSource ?? '', disabled: !secondDateSource });

  useEffect(() => {

    if (!firstDateSource || !secondDateSource || !firstValue || !secondValue) return;

    trigger([firstDateSource, secondDateSource]);

  }, [firstValue, secondValue, firstDateSource, secondDateSource, trigger]);

};

export const ExpiryDateDisplay = (props: {
  startDateSource: 'lastInspectionDate' | 'lastReceivedOn' | `certificationsByAssetId.${string}.lastReceivedOn`;
  expiryDateSource: 'expiryDate' | `certificationsByAssetId.${string}.expiryDate`;
  timeGranularity?: ExpiryTimeGranularity;

  startDateLabel?: string;
  expiryDateLabel?: string;
  helperText?: string;
  children?: React.ReactNode;
  usage: 'certifications' | 'inventoryItems' | 'table';
  isRequired?: boolean;
  minDateSource?: string;
  hideFieldErrors?: boolean;
  startDateValidate?: Validator;
  isProcessing?: boolean;
}) => {

  const fieldSize = props.usage === 'table' ? 'small' : 'medium';
  const translate = useTranslate();
  const smallerFieldWidth = props.usage === 'table';
  const startDateLabel = props.startDateLabel ?? false;
  const expiryDateLabel = props.expiryDateLabel ?? false;

  const { setValue, trigger } = useFormContext();
  const { getOrigin, setOrigin } = useCertificateDateOriginsContext();

  const {
    calculatedExpiryDate, applyExpiryDate, isOverridden, startDate, expiryDate,
  } = useExpiryDate(props.startDateSource, props.expiryDateSource, props.timeGranularity);

  const timeGranularityRounded = useMemo(() => {

    if (!startDate || !expiryDate) {

      return props.timeGranularity ?? undefined;

    }

    return getExpiryTimeGranularity(startDate, expiryDate);

  }, [startDate, expiryDate, props.timeGranularity]);

  const minExpiryDate = useMemo(() => {

    if (!startDate) return undefined;
    return getMinimumNextDate(startDate);

  }, [startDate]);

  const minDate = useWatch({
    name: props.minDateSource ?? '',
    disabled: !props.minDateSource,
  });

  const hasStartDateValidate = Boolean(props.startDateValidate);

  useEffect(() => {

    if (!hasStartDateValidate) return;

    trigger(props.startDateSource);

  }, [startDate, props.startDateSource, hasStartDateValidate, trigger]);

  useRevalidateDatePair(props.startDateSource, props.minDateSource);

  const isFlexibleWidth = props.usage === 'inventoryItems';
  const inputHeight = fieldSize === 'small' ? 40 : 56;
  const chipTopMargin = fieldSize === 'small' ? 0 : 'calc(0.5em + 16px + 0.15em)';
  const dateInputWidth = '100%';

  // this part is used to set the grid template columns for the date inputs and the expiry date input
  // if the width is flexible, date inputs take the remaining space
  const gridTemplateColumns = smallerFieldWidth
    ? TABLE_EXPIRY_GRID_TEMPLATE
    : [
      'minmax(0, 1fr)',
      'max-content',
      'minmax(0, 1fr)',
      ...(props.children ? ['max-content'] : []),
    ].join(' ');

  return (
    <Stack sx={{ width: '100%', minWidth: 0 }}>
      <Box sx={{
        display: 'grid',
        gridTemplateColumns,
        columnGap: 2,
        width: '100%',
        minWidth: 0,
      }}>
        <Box sx={{ minWidth: 0, width: '100%' }}>
          <GrayDateInputWithUpperLabel
            source={props.startDateSource}
            label={startDateLabel}
            size={fieldSize}
            helperText={false}
            onChange={(event) => {

              applyExpiryDate(event.target.value);
              if (isCertificateDateSource(props.startDateSource)) setOrigin(props.startDateSource, 'manual');

            }}
            sx={{
              width: '100%',
              ...(props.hideFieldErrors
                ? { '& .MuiFormHelperText-root': { display: 'none' } }
                : {}),
            }}
            inputProps={{
              ...(props.minDateSource && minDate
                ? { min: moment(minDate).format('YYYY-MM-DD') }
                : {}),
            }}
            validate={[required(), ...(props.startDateValidate ? [props.startDateValidate] : [])]}
            isRequired={props.isRequired}
            isProcessing={props.isProcessing}
            isProcessed={isCertificateDateSource(props.startDateSource) ? getOrigin(props.startDateSource) === 'ai' : false}
          />
        </Box>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: smallerFieldWidth ? 1 : 2,
            alignSelf: 'start',
            mt: chipTopMargin,
            height: inputHeight,
          }}
        >
          <Typography component="span" sx={{ fontWeight: 'bold', lineHeight: 1 }}>+</Typography>
          <Tooltip title={calculatedExpiryDate !== expiryDate ? translate('resources.expiry_date_display.text.click_to_reset_expiry') : undefined}>
            <Chip
              label={TimeGranularityLabel(timeGranularityRounded, translate)}
              onClick={calculatedExpiryDate ? () => applyExpiryDate() : undefined}
              clickable={calculatedExpiryDate !== expiryDate}
              color={isOverridden ? 'warning' : 'primary'}
            />
          </Tooltip>
          <Typography component="span" sx={{ fontWeight: 'bold', lineHeight: 1 }}>=</Typography>
        </Box>
        <Box sx={{ minWidth: 0, width: '100%' }}>
          <GrayDateInputWithUpperLabel
            source={props.expiryDateSource}
            label={expiryDateLabel}
            size={fieldSize}
            helperText={false}
            inputProps={{ min: minExpiryDate }}
            sx={{
              width: dateInputWidth,
              ...(props.hideFieldErrors
                ? { '& .MuiFormHelperText-root': { display: 'none' } }
                : {}),
            }}
            validate={required()}
            isRequired={props.isRequired}
            isProcessing={props.isProcessing}
            isProcessed={getOrigin(props.expiryDateSource) === 'ai'}
            onChange={(event) => {

              // shouldValidate is disabled to not show "required" hint under the field until the form is submitted
              if (event.target.value === '') setValue(props.expiryDateSource, null, { shouldDirty: true });
              else setValue(props.expiryDateSource, event.target.value, { shouldDirty: true, shouldValidate: true });

              setOrigin(props.expiryDateSource, 'manual');

            }}
          />
          {props.usage === 'inventoryItems' && (
            <Box sx={{ mt: 0.5 }}>
              <ExpiryDateHelperText text={props.helperText ?? ''} centerAlign/>
            </Box>
          )}
        </Box>
        {props.children && (
          <Box sx={{
            marginLeft: isFlexibleWidth ? 0 : 1,
            alignSelf: 'flex-start',
            minWidth: 0,
          }}>
            {props.children}
          </Box>
        )}
      </Box>
      {props.usage === 'certifications' && (
        <Box sx={{ maxWidth: 450 }}>
          <ExpiryDateHelperText text={props.helperText ?? ''} />
        </Box>
      )}
    </Stack>
  );

};
